import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MailOpen } from 'lucide-react';
import { parseAsBoolean, parseAsInteger, useQueryStates } from 'nuqs';
import { toast } from 'sonner';
import type { Paginated } from '@barbeiropro/contracts';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime, maskPhone } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { cn } from '@/lib/utils';
import type { MessageRow } from './types';

export function MessagesTab() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const [f, setF] = useQueryStates({ pm: parseAsInteger.withDefault(1), naolidas: parseAsBoolean.withDefault(false) }, { history: 'replace' });
  const q = useQuery({
    queryKey: ['site', 'messages', f],
    queryFn: () => api.get<Paginated<MessageRow> & { unread: number }>('/site/messages', { page: f.pm, pageSize: 20, unread: f.naolidas ? 'true' : undefined }),
    placeholderData: (p) => p,
  });

  async function mark(m: MessageRow, read: boolean) {
    try {
      await api.patch(`/site/messages/${m.id}`, { read });
      await qc.invalidateQueries({ queryKey: ['site', 'messages'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.pageSize)) : 1;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Mensagens enviadas pelo formulário de contato do site.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-[var(--bottle)]" checked={f.naolidas} onChange={(e) => setF({ naolidas: e.target.checked, pm: 1 })} />
          Só não lidas{q.data ? ` (${q.data.unread})` : ''}
        </label>
      </div>
      {q.isPending && <LoadingState />}
      {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && q.data.data.length === 0 && <EmptyState title={f.naolidas ? 'Nenhuma mensagem não lida' : 'Nenhuma mensagem ainda'} description="Quando alguém usar o formulário de contato, a mensagem aparece aqui." />}
      <ul className="grid gap-2">
        {q.data?.data.map((m) => (
          <li key={m.id} className={cn('rounded-lg border bg-card p-4', !m.readAt && 'border-primary/50 bg-accent/40')}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {m.name}
                  {!m.readAt && <Badge>Nova</Badge>}
                </p>
                <p className="text-sm text-muted-foreground">
                  <a className="underline-offset-2 hover:underline" href={`mailto:${m.email}`}>{m.email}</a>
                  {m.phone ? ` · ${maskPhone(m.phone)}` : ''} · {fmtDateTime(m.createdAt, tz)}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => mark(m, !m.readAt)}>
                {m.readAt ? <><Mail aria-hidden /> Marcar como não lida</> : <><MailOpen aria-hidden /> Marcar como lida</>}
              </Button>
            </div>
            <p className="mt-3 whitespace-pre-line text-sm">{m.message}</p>
          </li>
        ))}
      </ul>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={f.pm <= 1} onClick={() => setF({ pm: f.pm - 1 })}>Anterior</Button>
          <span className="tabular">{f.pm} de {pages}</span>
          <Button size="sm" variant="outline" disabled={f.pm >= pages} onClick={() => setF({ pm: f.pm + 1 })}>Próxima</Button>
        </div>
      )}
    </div>
  );
}
